import React from 'react';
import { Helmet } from 'react-helmet-async';
import CtaStrip from '../components/CtaStrip';
import Footer from '../components/Footer';

const services = [
  { icon:'🔌', title:'Plugin Development', accent:'var(--ms-blue)', iconBg:'var(--ms-blue-pale)',
    desc:'Custom C# plugins on create, update, and delete events. Enforce complex business logic server-side with full CRM SDK access, clean architecture, and tested code.' },
  { icon:'📜', title:'Web Resources & Scripting', accent:'var(--ms-teal)', iconBg:'#DFF6F0',
    desc:'JavaScript form scripts, ribbon customisations, and HTML web resources that shape D365 UI behaviour and extend the user experience to match your processes.' },
  { icon:'🧩', title:'PCF Control Development', accent:'var(--ms-purple)', iconBg:'#F4F0FF',
    desc:'Custom PowerApps Component Framework controls that replace standard D365 fields with rich, interactive UI components — charts, maps, pickers, and more.' },
  { icon:'🤖', title:'PVA Chatbot Development', accent:'var(--ms-orange)', iconBg:'#FFF1EC',
    desc:'Power Virtual Agents chatbots that handle customer service queries, qualify leads, and trigger D365 actions — fully integrated with your CRM and available 24/7.' },
  { icon:'⚡', title:'Power Automate & Workflows', accent:'var(--ms-blue-dark)', iconBg:'#EFF6FC',
    desc:'Cloud flows, business process flows, and classic workflows that automate approvals, cross-system notifications, and repetitive processes at scale.' },
  { icon:'📦', title:'Data Migration & Import', accent:'var(--ms-green)', iconBg:'#DFF6D7',
    desc:'Clean, map, and migrate data from legacy CRMs, spreadsheets, or external databases into D365 with full validation rules and rollback planning in place.' },
  { icon:'🛠️', title:'CRM Support & Maintenance', accent:'var(--ms-red)', iconBg:'#FDE7E9',
    desc:'Ongoing support packages for your existing D365 environment. Bug fixes, performance tuning, user query resolution, and incremental enhancements on demand.' },
  { icon:'🔗', title:'Third-Party Integration', accent:'var(--ms-cyan)', iconBg:'#E0F7FA',
    desc:'Connect D365 to ERP, accounting, marketing platforms, or any REST-enabled service via Azure Functions, middleware, or custom Power Platform connectors.' },
];

const steps = [
  { num:'1', title:'Discovery', desc:'We walk through requirements, your D365 environment, and any constraints in detail.' },
  { num:'2', title:'Scope & Quote', desc:'Clear deliverables, a fixed price, and a realistic timeline — all agreed upfront.' },
  { num:'3', title:'Development', desc:'Iterative builds with regular updates and a staging environment for your review.' },
  { num:'4', title:'Handover', desc:'Full testing, documentation, deployment, and post-go-live support included.' },
];

export default function ServicesPage({ onNavigate }) {
  return (
    <div>
      <Helmet>
        <title>D365 Customisation Services — Plugin Dev, PCF, Power Automate | AdjustD365</title>
        <meta name="description" content="Dynamics 365 customisation services including plugin development, PCF controls, Power Automate flows, PVA chatbots, data migration, and third-party integrations. Fixed price, fast delivery." />
        <link rel="canonical" href="https://adjustd365.com/services" />
      </Helmet>
      {/* PAGE HERO */}
      <div className="section-pad" style={{ background:'var(--bg-2)', borderBottom:'1px solid var(--border)', padding:'56px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div style={{ fontSize:'13px', color:'var(--text-s)', marginBottom:'16px', display:'flex', alignItems:'center', gap:'6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background:'none', border:'none', color:'var(--text-s)', cursor:'pointer', fontSize:'13px' }}>Home</button>
            <span>›</span>
            <span style={{ color:'var(--ms-blue)', fontWeight:500 }}>Services</span>
          </div>
          <h1 style={{ fontSize:'clamp(26px,3vw,42px)', fontWeight:700, letterSpacing:'-0.02em', color:'var(--text-h)', marginBottom:'12px' }}>
            D365 Customisation & Support Services
          </h1>
          <p style={{ fontSize:'16px', color:'var(--text-m)', maxWidth:'580px' }}>
            From quick-win scripts to complex integrations and chatbot solutions — delivered on time, on spec, and fully documented.
          </p>
        </div>
      </div>

      {/* SERVICES GRID */}
      <section className="section-pad" style={{ padding:'80px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div className="grid-3col-tight" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
            {services.map((s, i) => (
              <div
                key={i}
                style={{
                  background:'#fff', border:'1px solid var(--border)', borderRadius:'6px',
                  padding:'28px', position:'relative', overflow:'hidden', transition:'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,120,212,0.1)'; e.currentTarget.style.borderColor = 'var(--ms-blue)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                {/* Left accent bar */}
                <div style={{ position:'absolute', top:0, left:0, width:'4px', height:'100%', background:s.accent }} />
                <div style={{ width:'44px', height:'44px', borderRadius:'6px', background:s.iconBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px', marginBottom:'18px' }}>
                  {s.icon}
                </div>
                <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-h)', marginBottom:'10px' }}>{s.title}</div>
                <p style={{ fontSize:'13px', color:'var(--text-m)', lineHeight:1.65, marginBottom:'20px' }}>{s.desc}</p>
                <button
                  onClick={() => onNavigate('contact')}
                  style={{ fontSize:'13px', fontWeight:600, color:'var(--ms-blue)', background:'none', border:'none', cursor:'pointer', padding:0 }}
                >
                  Enquire →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <div className="section-pad" style={{ background:'var(--ms-blue-darker)', padding:'72px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div style={{ fontSize:'12px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', marginBottom:'10px' }}>My Process</div>
          <h2 style={{ fontSize:'clamp(24px,2.8vw,36px)', fontWeight:700, letterSpacing:'-0.02em', color:'#fff', marginBottom:'0' }}>How every project runs</h2>
          <div className="grid-4col" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'32px', marginTop:'40px', position:'relative' }}>
            {/* Connector line */}
            <div className="process-line" style={{ position:'absolute', top:'27px', left:'calc(12.5% + 8px)', right:'calc(12.5% + 8px)', height:'1px', background:'rgba(255,255,255,0.15)' }} />
            {steps.map((s, i) => (
              <div key={i} style={{ textAlign:'center' }}>
                <div style={{
                  width:'56px', height:'56px', borderRadius:'50%',
                  background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'20px', fontWeight:700, color:'#fff',
                  margin:'0 auto 18px', position:'relative', zIndex:1,
                }}>
                  {s.num}
                </div>
                <div style={{ fontSize:'15px', fontWeight:600, color:'#fff', marginBottom:'6px' }}>{s.title}</div>
                <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.6)', lineHeight:1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CtaStrip
        heading="Ready to extend your D365?"
        subtext="Tell me what you need and I'll respond within 24 hours with a clear plan."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
